import React, {
  createContext,
  useContext,
  useCallback,
  ReactNode
} from 'react';
import type { ExternalReference } from '../types';
import { useRedirectUrl, useSessionData, useExternalReference } from '../hooks';

interface AuthenticationFlowContextValue {
  // Storage-backed state (automatically persists across external redirects)
  redirectUrl: string | null;
  sessionId: string | null;
  systemId: string | null;

  // External reference state (derived from URL parameters)
  externalReference: ExternalReference | null;
  hasExternalReference: boolean;

  // Actions
  setRedirectUrl: (url: string | null) => void;
  setSessionId: (id: string | null) => void;
  setSystemId: (id: string | null) => void;
  clearExternalReferenceParams: () => void;
  clearState: () => void;
}

const AuthenticationFlowContext =
  createContext<AuthenticationFlowContextValue | null>(null);

interface AuthenticationFlowProviderProps {
  children: ReactNode;
}

/**
 * Authentication Flow Context Provider
 * Manages multi-component shared state for authentication flow
 * Uses storage-based hooks for automatic persistence across external redirects
 */
export function AuthenticationFlowProvider({
  children
}: AuthenticationFlowProviderProps) {
  // Storage-backed state (persists across external redirects)
  const [redirectUrl, setRedirectUrl] = useRedirectUrl();
  const { sessionId, systemId, setSessionId, setSystemId } = useSessionData();

  // External reference state (derived from URL parameters)
  const {
    externalReference,
    hasExternalReference,
    clearExternalReferenceParams
  } = useExternalReference();

  const clearState = useCallback(() => {
    setRedirectUrl(null);
    setSessionId(null);
    setSystemId(null);
  }, [setRedirectUrl, setSessionId, setSystemId]);

  const contextValue: AuthenticationFlowContextValue = {
    // Storage-backed state (automatically synced)
    redirectUrl,
    sessionId,
    systemId,

    // External reference state (derived from URL parameters)
    externalReference,
    hasExternalReference,

    // Actions
    setRedirectUrl,
    setSessionId,
    setSystemId,
    clearExternalReferenceParams,
    clearState
  };

  return (
    <AuthenticationFlowContext.Provider value={contextValue}>
      {children}
    </AuthenticationFlowContext.Provider>
  );
}

/**
 * Hook to access authentication flow context
 * Throws error if used outside provider (following React best practices)
 */
export function useAuthenticationFlow(): AuthenticationFlowContextValue {
  const context = useContext(AuthenticationFlowContext);
  if (!context) {
    throw new Error(
      'useAuthenticationFlow must be used within AuthenticationFlowProvider'
    );
  }
  return context;
}
